import { apiGet, apiPatch } from './client';
import type {
  CapitalConsolidation,
  CapitalConsolidationBatchRequest,
  CapitalConsolidationKey,
} from '../types/CapitalConsolidation';

// Server returns rows without `idKey` — the page needs a single key for the
// grid. Synthesize `fundId|dDate` here so callers (LopCrudPage idField) work
// uniformly. Same recipe as CalcMethodMap / AssetRatesHistory.
function withIdKey(
  rows: Omit<CapitalConsolidation, 'idKey'>[],
): CapitalConsolidation[] {
  return rows.map((r) => ({ ...r, idKey: `${r.fundId}|${r.dDate}` }));
}

function stripIdKey(
  rows: CapitalConsolidation[],
): Omit<CapitalConsolidation, 'idKey'>[] {
  return rows.map(({ idKey, ...rest }) => rest);
}

function parseIdKey(idKey: string): CapitalConsolidationKey {
  const [fundId, dDate] = idKey.split('|');
  return { fundId: Number(fundId), dDate };
}

// Tenant-scoped (X-Tenant-Id applied automatically by the client).
export async function getCapitalConsolidations(): Promise<
  CapitalConsolidation[]
> {
  const rows = await apiGet<Omit<CapitalConsolidation, 'idKey'>[]>(
    '/api/capital-consolidations',
  );
  return withIdKey(rows);
}

export async function saveCapitalConsolidationsBatch(
  added: CapitalConsolidation[],
  modified: CapitalConsolidation[],
  deletedIdKeys: string[],
): Promise<CapitalConsolidation[]> {
  const body: CapitalConsolidationBatchRequest = {
    added: stripIdKey(added),
    modified: stripIdKey(modified),
    deleted: deletedIdKeys.map(parseIdKey),
  };
  const fresh = await apiPatch<
    CapitalConsolidationBatchRequest,
    Omit<CapitalConsolidation, 'idKey'>[]
  >('/api/capital-consolidations/batch', body);
  return withIdKey(fresh);
}
